import { useEffect, useRef, useState } from "react";
import {
  sendTurn,
  analyzeUtteranceApi,
  startSession as apiStartSession,
  endSession as apiEndSession,
  scorePronunciation,
  type ChatMessage,
  type TurnAnalysis,
  type SessionSummary as SummaryT,
  type UserRow,
  type PronunciationResult,
} from "../lib/api";
import { STT } from "../lib/stt";
import { TTS } from "../lib/tts";
import { SimliAvatar } from "./SimliAvatar";
import { Webcam } from "./Webcam";
import { MicButton } from "./MicButton";
import { CorrectionBubble } from "./CorrectionBubble";
import { PronunciationBubble } from "./PronunciationBubble";
import { SessionSummary } from "./SessionSummary";
import type { SimliAvatarClient } from "../lib/simli";

type Phase = "idle" | "listening" | "thinking" | "speaking";

interface Message extends ChatMessage {
  analysis?: TurnAnalysis | null;
  pronunciation?: PronunciationResult | null;
}

const STT_LANG = "en-US";

interface Props {
  user: UserRow;
  teacherName: string;
  mode?: "free" | "targeted" | "drill";
  unitPromptBlock?: string;
  unitSlug?: string;
  systemPromptBase: string;
  onExit: () => void;
}

function buildSystemPrompt(base: string, user: UserRow): string {
  const levelHint = user.level
    ? `\n\nThe learner's CEFR level is ${user.level}. Match your vocabulary and grammar complexity to this level.`
    : "";
  const nameHint = user.name ? `\n\nThe learner's name is ${user.name}.` : "";
  const goalHint = user.goal
    ? `\n\nTheir goal is: ${user.goal}. Keep activities relevant.`
    : "";
  return base + levelHint + nameHint + goalHint;
}

export function Lesson({ user, teacherName, systemPromptBase, onExit, mode = "free", unitPromptBlock, unitSlug }: Props): JSX.Element {
  const greeting = user.name
    ? `Hi ${user.name}! Great to see you. What would you like to talk about today?`
    : `Hi! I'm ${teacherName}, your English teacher. What would you like to talk about today?`;

  const systemPrompt = unitPromptBlock
    ? buildSystemPrompt(systemPromptBase, user) + "\n\n" + unitPromptBlock
    : buildSystemPrompt(systemPromptBase, user);

  const [messages, setMessages] = useState<Message[]>([]);
  const [phase, setPhase] = useState<Phase>("idle");
  const [interim, setInterim] = useState<string>("");
  const [error, setError] = useState<string | null>(null);
  const [showTranscript, setShowTranscript] = useState(true);
  const [conversationActive, setConversationActive] = useState(false);
  const [avatarReady, setAvatarReady] = useState(false);
  const [avatarError, setAvatarError] = useState<string | null>(null);
  const [summary, setSummary] = useState<SummaryT | null>(null);

  const sttRef = useRef<STT | null>(null);
  const ttsRef = useRef<TTS | null>(null);
  const simliRef = useRef<SimliAvatarClient | null>(null);
  const sttSupported = useRef<boolean>(false);
  const conversationActiveRef = useRef(false);
  const messagesRef = useRef<Message[]>([]);
  const sessionIdRef = useRef<string | null>(null);

  conversationActiveRef.current = conversationActive;
  messagesRef.current = messages;

  useEffect(() => {
    sttSupported.current = STT.isSupported();
    ttsRef.current = new TTS();
    return () => {
      ttsRef.current?.cancel();
      sttRef.current?.abort();
    };
  }, []);

  function handleAvatarReady(client: SimliAvatarClient): void {
    simliRef.current = client;
    ttsRef.current?.setSimli(client);
    setAvatarReady(true);
  }

  function scheduleAutoListen(delayMs = 250): void {
    if (!conversationActiveRef.current) return;
    setTimeout(() => {
      if (conversationActiveRef.current) startListening();
    }, delayMs);
  }

  async function speak(text: string): Promise<void> {
    if (!ttsRef.current) return;
    setPhase("speaking");
    await ttsRef.current.speak(text, {
      onEnd: () => {
        setPhase("idle");
        scheduleAutoListen(300);
      },
      onError: (err) => {
        setError(`TTS: ${err}`);
        setPhase("idle");
        scheduleAutoListen(500);
      },
    });
  }

  function toChatMessages(msgs: Message[]): ChatMessage[] {
    return msgs.map(({ role, content }) => ({ role, content }));
  }

  async function handleUserUtterance(text: string): Promise<void> {
    if (!text.trim()) return;
    setError(null);
    const userMsg: Message = { role: "user", content: text };
    const next: Message[] = [...messagesRef.current, userMsg];
    setMessages(next);
    setPhase("thinking");

    try {
      const payload: ChatMessage[] = [
        { role: "system", content: systemPrompt },
        ...toChatMessages(next),
      ];
      // Three things kick off in parallel, none block speaking:
      //   1. /api/turn — responder (LLM). We DO await this to know what to speak.
      //   2. /api/analyze — grammar correction. Surfaces in CorrectionBubble.
      //   3. /api/pronunciation/score — Speechace. Surfaces in PronunciationBubble.
      const audioBlob = sttRef.current?.getAudioBlob() ?? null;
      const pronPromise: Promise<PronunciationResult | null> =
        audioBlob && text.length > 1
          ? scorePronunciation(audioBlob, text).catch(() => null)
          : Promise.resolve(null);

      const analysisPromise = analyzeUtteranceApi(text, {
        sessionId: sessionIdRef.current ?? undefined,
        targetLang: user.target_lang,
        nativeLang: user.native_lang,
        recentTurns: messagesRef.current.slice(-6, -1).map((m) => ({ role: m.role, content: m.content })),
      });

      const { reply } = await sendTurn(payload, {
        sessionId: sessionIdRef.current ?? undefined,
        targetLang: user.target_lang,
        nativeLang: user.native_lang,
      });
      const after: Message[] = [
        ...messagesRef.current,
        { ...userMsg },
        { role: "assistant", content: reply },
      ];
      setMessages(after);
      void speak(reply);

      // Resolve grammar correction in background and attach when ready.
      void analysisPromise.then((analysis) => {
        if (!analysis) return;
        setMessages((curr) => {
          const idx = curr.findLastIndex((m) => m.role === "user");
          if (idx < 0) return curr;
          const next = [...curr];
          next[idx] = { ...next[idx], analysis };
          return next;
        });
      });

      // Same for pronunciation.
      void pronPromise.then((pron) => {
        if (!pron) return;
        setMessages((curr) => {
          const idx = curr.findLastIndex((m) => m.role === "user");
          if (idx < 0) return curr;
          const next = [...curr];
          next[idx] = { ...next[idx], pronunciation: pron };
          return next;
        });
      });
      return;
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setPhase("idle");
      scheduleAutoListen(800);
    }
  }

  function startListening(): void {
    if (!sttSupported.current) {
      setError("Speech recognition not supported. Please use Chrome or Edge.");
      setConversationActive(false);
      return;
    }
    setError(null);
    setInterim("");
    ttsRef.current?.cancel();

    const stt = new STT({
      lang: STT_LANG,
      captureAudio: true,
      onResult: (text, isFinal) => {
        if (isFinal) {
          setInterim("");
          void handleUserUtterance(text);
        } else {
          setInterim(text);
        }
      },
      onError: (err) => {
        if (err === "no-speech") {
          setPhase("idle");
          scheduleAutoListen(400);
          return;
        }
        if (err === "aborted") {
          setPhase("idle");
          return;
        }
        setError(`STT: ${err}`);
        setPhase("idle");
      },
      onEnd: () => {
        setInterim("");
      },
    });

    sttRef.current = stt;
    stt.start();
    setPhase("listening");
  }

  async function startConversation(): Promise<void> {
    if (!avatarReady) {
      setError("Avatar still loading…");
      return;
    }
    setSummary(null);
    setConversationActive(true);
    setMessages([{ role: "assistant", content: greeting }]);

    try {
      const { session_id } = await apiStartSession({
        targetLang: user.target_lang,
        nativeLang: user.native_lang,
      });
      sessionIdRef.current = session_id;
    } catch (err) {
      setError(`session start failed: ${err instanceof Error ? err.message : String(err)}`);
    }

    void (async () => {
      await speak(greeting);
    })();
  }

  async function endConversation(): Promise<void> {
    setConversationActive(false);
    ttsRef.current?.cancel();
    sttRef.current?.abort();
    setPhase("idle");
    setInterim("");

    const sid = sessionIdRef.current;
    sessionIdRef.current = null;
    if (sid) {
      try {
        const s = await apiEndSession(sid);
        setSummary(s);
      } catch (err) {
        setError(`session end failed: ${err instanceof Error ? err.message : String(err)}`);
      }
    }
  }

  function handleMicClick(): void {
    if (!conversationActive) {
      void startConversation();
    } else {
      void endConversation();
    }
  }

  const micState: Phase = !conversationActive ? "idle" : phase;
  const micLabel = !conversationActive
    ? avatarReady ? "Start conversation" : "Loading avatar…"
    : phase === "listening"
      ? "Listening…"
      : phase === "thinking"
        ? "Thinking…"
        : phase === "speaking"
          ? `${teacherName} is speaking…`
          : "End conversation";

  return (
    <div className="relative h-full w-full bg-black overflow-hidden">
      {/* ---------- FULL-SCREEN AVATAR LAYER ---------- */}
      <div className="absolute inset-0">
        <SimliAvatar
          onReady={handleAvatarReady}
          onError={(err) => setAvatarError(err)}
        />
      </div>

      {/* ---------- PHASE HALO (pulses around the avatar when listening / speaking) ---------- */}
      <div className="pointer-events-none absolute inset-0">
        {conversationActive && (
          <div
            className={`absolute inset-0 transition-opacity duration-500 ${
              phase === "listening"
                ? "opacity-100 shadow-[inset_0_0_120px_rgba(132,204,22,0.35)]"
                : phase === "speaking"
                  ? "opacity-100 shadow-[inset_0_0_120px_rgba(96,165,250,0.35)]"
                  : "opacity-0"
            }`}
            style={{ boxShadow: phase === "listening"
              ? "inset 0 0 140px 0 rgba(132,204,22,0.35)"
              : phase === "speaking"
                ? "inset 0 0 140px 0 rgba(96,165,250,0.30)"
                : "none" }}
          />
        )}
      </div>

      {/* ---------- LOADING / ERROR OVERLAY ---------- */}
      {!avatarReady && !avatarError && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="flex flex-col items-center gap-3">
            <div className="w-10 h-10 border-2 border-ink-600 border-t-lime-400 rounded-full animate-spin" />
            <div className="text-ink-300 text-xs uppercase tracking-widest">Loading avatar…</div>
          </div>
        </div>
      )}
      {avatarError && (
        <div className="absolute inset-0 flex items-center justify-center p-6 pointer-events-none">
          <div className="bg-red-900/60 border border-red-700 text-red-100 rounded-xl px-4 py-3 text-sm max-w-md text-center">
            Couldn{"\u2019"}t load avatar: {avatarError}
          </div>
        </div>
      )}

      {/* ---------- FLOATING TOP BAR ---------- */}
      <div className="absolute top-3 left-3 right-3 flex items-center justify-between z-20 pointer-events-none">
        <button
          onClick={onExit}
          className="pointer-events-auto rounded-full bg-black/60 backdrop-blur border border-white/10 text-white/90 hover:bg-black/80 text-xs px-3 py-1.5 flex items-center gap-1.5"
          title="Back to dashboard"
        >
          <span aria-hidden>{"\u2190"}</span>
          <span>Dashboard</span>
        </button>

        <div className="pointer-events-none rounded-full bg-black/60 backdrop-blur border border-white/10 px-3 py-1 text-center">
          <div className="text-xs font-medium text-white">{teacherName}</div>
          <div className="text-[9px] uppercase tracking-widest text-white/60">
            level {user.level ?? "—"} {"\u00b7"} {user.target_lang}
          </div>
        </div>

        <button
          onClick={() => setShowTranscript((v) => !v)}
          className="pointer-events-auto rounded-full bg-black/60 backdrop-blur border border-white/10 text-white/90 hover:bg-black/80 text-xs px-3 py-1.5"
          title="Toggle transcript"
        >
          {showTranscript ? "Hide transcript" : "Show transcript"}
        </button>
      </div>

      {/* ---------- PHASE / STATUS CHIP (top center, subtle) ---------- */}
      {conversationActive && (
        <div className="absolute top-16 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
          <div
            className={`rounded-full px-3 py-1 text-[11px] uppercase tracking-widest backdrop-blur border ${
              phase === "listening"
                ? "border-lime-400/60 bg-lime-400/15 text-lime-200"
                : phase === "speaking"
                  ? "border-sky-400/60 bg-sky-400/15 text-sky-200"
                  : phase === "thinking"
                    ? "border-white/30 bg-black/40 text-white/80"
                    : "border-white/20 bg-black/30 text-white/60"
            }`}
          >
            {phase === "listening" ? "● Listening" : phase === "speaking" ? `● ${teacherName} speaking` : phase === "thinking" ? "● Thinking" : "Ready"}
          </div>
        </div>
      )}

      {/* ---------- WEBCAM PiP (bottom-left, small, rounded) ---------- */}
      <div className="absolute bottom-4 left-4 w-32 sm:w-40 aspect-[4/3] z-20 rounded-2xl overflow-hidden border border-white/10 shadow-2xl">
        <Webcam />
      </div>

      {/* ---------- INTERIM TRANSCRIPT BUBBLE ---------- */}
      {interim && (
        <div className="absolute bottom-28 left-1/2 -translate-x-1/2 z-20 pointer-events-none max-w-md">
          <div className="bg-black/70 backdrop-blur border border-lime-500/40 text-white px-4 py-2 rounded-full text-sm text-center">
            {"\u201c"}{interim}{"\u201d"}
          </div>
        </div>
      )}

      {/* ---------- MIC BUTTON — floating bottom-center ---------- */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-30 flex flex-col items-center gap-2">
        <MicButton state={micState} onClick={handleMicClick} customLabel={micLabel} />
        {!conversationActive && avatarReady && (
          <p className="text-white/70 text-xs text-center max-w-xs drop-shadow-lg">
            Tap once. {teacherName} will greet you and start listening.
          </p>
        )}
        {error && (
          <div className="bg-red-900/70 backdrop-blur border border-red-700 text-red-100 rounded-xl px-4 py-2 text-xs max-w-md text-center">
            {error}
          </div>
        )}
        {!sttSupported.current && (
          <div className="bg-yellow-900/70 backdrop-blur border border-yellow-700 text-yellow-100 rounded-xl px-4 py-2 text-xs max-w-md text-center">
            Speech recognition not supported. Try Chrome or Edge.
          </div>
        )}
      </div>

      {/* ---------- TRANSCRIPT SIDE DRAWER ---------- */}
      <aside
        className={`absolute top-0 right-0 h-full w-full sm:w-[28rem] z-40 bg-ink-950/95 backdrop-blur-xl border-l border-white/10 overflow-y-auto p-4 transition-transform duration-300 ${
          showTranscript ? "translate-x-0" : "translate-x-full"
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-xs uppercase tracking-widest text-white/60">Transcript</h2>
          <button
            onClick={() => setShowTranscript(false)}
            className="text-white/60 hover:text-white text-xl leading-none"
            aria-label="Close"
          >
            {"\u00d7"}
          </button>
        </div>
        {messages.length === 0 && (
          <p className="text-white/40 text-sm">No messages yet. Tap the microphone.</p>
        )}
        <div className="space-y-3">
          {messages.map((m, i) => (
            <div key={i}>
              <div
                className={`rounded-xl px-3 py-2 text-sm ${
                  m.role === "user"
                    ? "bg-lime-500/15 border border-lime-500/30 text-white"
                    : "bg-white/5 border border-white/10 text-white/90"
                }`}
              >
                <div className="text-[10px] uppercase tracking-wider opacity-60 mb-1">
                  {m.role === "user" ? "You" : teacherName}
                </div>
                {m.content}
              </div>
              {m.role === "user" && m.analysis && (
                <CorrectionBubble userText={m.content} analysis={m.analysis} />
              )}
              {m.role === "user" && m.pronunciation && (
                <PronunciationBubble result={m.pronunciation} />
              )}
            </div>
          ))}
        </div>
      </aside>

      {summary && (
        <SessionSummary
          summary={summary}
          onClose={() => setSummary(null)}
          teacherName={teacherName}
        />
      )}
    </div>
  );
}
